import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarClock, CheckCheck, CheckCircle2, MessageSquare, Send, X, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import api, { API } from "@/lib/api";
import { frDate, platformLabel, formatLabel, isVideo } from "@/lib/constants";
import CarouselViewer from "@/components/CarouselViewer";
import ClientContentRequests from "@/components/ClientContentRequests";
import ClientPushSettings from "@/components/ClientPushSettings";
import { NewRequestButton, ClientRequestsList } from "@/components/ClientNewRequest";
import FeedGrid from "@/components/FeedGrid";
import CalendarMonth from "@/components/CalendarMonth";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const pubComments = (p) => (p.comments || []);

function PubCard({ p, scrollId = false, client, changesPub, comment, setComment, setChangesPub, onValidate, onRequestChanges }) {
  return (
    <div id={scrollId ? `review-pub-${p.id}` : undefined} className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden animate-fade-up scroll-mt-24" data-testid={`review-pub-${p.id}`}>
      <div className="flex items-center gap-3 px-4 sm:px-5 py-4">
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{p.title}</p>
            <p className="text-xs text-neutral-400">{frDate(p.date)} · {p.time} · {platformLabel(p.platform)} {formatLabel(p.format)}</p>
          </div>
          {p.client_notice && (
            <span className="flex items-center gap-1 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2.5 py-1" data-testid={`review-notice-badge-${p.id}`}>
              <RefreshCw className="w-3 h-3" /> Nouvelle version
            </span>
          )}
          {p.status === "valide" ? (
            <span className="flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1"><CheckCircle2 className="w-3.5 h-3.5" /> Validé</span>
          ) : p.status === "programme" ? (
            <span className="flex items-center gap-1 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2.5 py-1"><CalendarClock className="w-3.5 h-3.5" /> Programmée</span>
          ) : p.status === "publie" ? (
            <span className="flex items-center gap-1 text-xs text-neutral-700 bg-neutral-100 border border-neutral-200 rounded-full px-2.5 py-1"><CheckCheck className="w-3.5 h-3.5" /> Publiée</span>
          ) : p.status === "modification" ? (
            <span className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-1">Modification demandée</span>
          ) : (
            <span className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-full px-2.5 py-1">À valider</span>
          )}
      </div>

      <div className="px-4 sm:px-5 pb-5 space-y-4 border-t border-neutral-100 pt-4">
          {p.client_notice && (
            <div className="flex items-center gap-2.5 bg-blue-50 border border-blue-200 text-blue-800 rounded-xl px-4 py-3 text-sm" data-testid="review-new-version-notice">
              <RefreshCw className="w-4 h-4 shrink-0" />
              Une nouvelle version de cette publication vient d'être envoyée par votre community manager. Merci de la valider ou de préciser vos retours.
            </div>
          )}
          <div className="relative rounded-xl overflow-hidden bg-neutral-100 max-w-sm mx-auto" data-testid="review-visual">
            {p.format === "carrousel" && (p.slides || []).length > 0 ? (
              <CarouselViewer slides={p.slides} title={p.title} testid={`review-carousel-${p.id}`} />
            ) : p.visual_url ? (
              isVideo(p.visual_url) ? (
                <>
                  <video src={p.visual_url} controls playsInline className="w-full aspect-[9/16] object-contain bg-black" data-testid="review-visual-video" />
                  <a href={p.visual_url} download target="_blank" rel="noreferrer" data-testid="review-download-video"
                    className="block text-center text-xs text-neutral-500 hover:text-neutral-900 py-2 border-t border-neutral-100 transition-colors">
                    Télécharger la vidéo
                  </a>
                </>
              ) : (
                <img src={p.visual_url} alt={p.title} className="w-full" data-testid="review-visual-img" />
              )
            ) : (
              <div className="aspect-[4/5] flex items-center justify-center text-white font-heading font-bold text-xl p-6 text-center" style={{ background: client.color }}>
                {p.title}
              </div>
            )}
          </div>

          {p.description && (
            <div>
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-1">Description</p>
              <p className="text-sm text-neutral-700 whitespace-pre-line">{p.description}</p>
            </div>
          )}
          {p.hashtags && <p className="text-sm text-blue-600 break-words">{p.hashtags}</p>}
          {p.cta && <p className="text-sm font-medium">{p.cta}</p>}

          <div className="space-y-3 pt-2">
            {pubComments(p).map((c) => (
              <div key={c.id} className="flex gap-2.5" data-testid={`review-comment-${c.id}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${
                  c.author_type === "client" ? "text-white" : "bg-neutral-900 text-white"}`}
                  style={c.author_type === "client" ? { background: client.color } : {}}>
                  {c.author_name?.[0]?.toUpperCase()}
                </div>
                <div className="bg-neutral-50 border border-neutral-100 rounded-lg px-3 py-2 flex-1 min-w-0">
                  <p className="text-xs font-medium">{c.author_name}</p>
                  <p className="text-sm text-neutral-600 break-words">{c.text}</p>
                </div>
              </div>
            ))}
          </div>

          {changesPub === p.id ? (
            <div className="border border-red-200 bg-red-50/50 rounded-xl p-4 space-y-3" data-testid="review-changes-form">
              <p className="text-sm font-medium text-red-700">Qu'est-ce qui doit être modifié ?</p>
              <Textarea data-testid="review-changes-input" rows={3} autoFocus value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Ex : Peut-on remplacer cette photo ?" />
              <div className="flex gap-2">
                <Button data-testid="review-changes-submit" onClick={() => onRequestChanges(p)}
                  className="bg-red-600 hover:bg-red-700 text-white gap-1.5 flex-1">
                  <Send className="w-4 h-4" /> Envoyer la demande
                </Button>
                <Button variant="outline" data-testid="review-changes-cancel"
                  onClick={() => { setChangesPub(null); setComment(""); }}>
                  Annuler
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Button data-testid="review-validate-btn" onClick={() => onValidate(p)} disabled={["valide", "programme", "publie"].includes(p.status)}
                className="bg-green-600 hover:bg-green-700 text-white gap-1.5 flex-1">
                <CheckCircle2 className="w-4 h-4" /> Valider
              </Button>
              <Button data-testid="review-changes-btn" onClick={() => { setChangesPub(p.id); setComment(""); }} variant="outline"
                className="gap-1.5 flex-1 border-red-200 text-red-600 hover:bg-red-50">
                <MessageSquare className="w-4 h-4" /> Demander une modification
              </Button>
            </div>
          )}
        </div>
    </div>
  );
}

export default function Review() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState("valider");
  const [name, setName] = useState(localStorage.getItem("sf-reviewer") || "");
  const [comment, setComment] = useState("");
  const [changesPub, setChangesPub] = useState(null);
  const [month, setMonth] = useState(new Date());
  const [detailPub, setDetailPub] = useState(null);
  const [validerSub, setValiderSub] = useState("pubs");
  const [valideeSub, setValideeSub] = useState("validees");

  const load = () => {
    api.get(`/review/${token}`).then((r) => setData(r.data)).catch(() => setError(true));
  };
  useEffect(load, [token]);
  // PWA personnalisée : manifest, icône (logo du client) et titre propres à ce client
  useEffect(() => {
    if (!data?.client) return;
    const c = data.client;
    const base = `${API}/review/${token}`;
    const setLink = (rel, href) => {
      let el = document.querySelector(`head link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const setMeta = (name, content) => {
      let el = document.querySelector(`head meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setLink("manifest", `${base}/manifest.json`);
    setLink("apple-touch-icon", `${base}/icon/180`);
    setLink("icon", `${base}/icon/192`);
    setMeta("theme-color", c.color || "#171717");
    setMeta("apple-mobile-web-app-title", c.name || "Espace client");
    document.title = `${c.name} — Espace de validation`;
  }, [data, token]);
  useEffect(() => {
    if (detailPub && data) {
      const fresh = data.publications.find((x) => x.id === detailPub.id);
      if (fresh) setDetailPub(fresh);
    }
  }, [data]);

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6">
        <div className="text-center">
          <p className="font-heading font-bold text-xl">Lien invalide</p>
          <p className="text-sm text-neutral-500 mt-1">Ce lien de validation n'existe pas ou a expiré.</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-neutral-50 flex items-center justify-center"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>;
  }

  const { client, publications } = data;
  const toValidate = publications.filter((p) => p.status === "a_valider");
  const toValidatePubs = toValidate.filter((p) => p.format !== "story");
  const toValidateStories = toValidate.filter((p) => p.format === "story");
  const validerList = validerSub === "stories" ? toValidateStories : toValidatePubs;
  const pendingLabel = [
    toValidatePubs.length > 0 && `${toValidatePubs.length} publication${toValidatePubs.length > 1 ? "s" : ""}`,
    toValidateStories.length > 0 && `${toValidateStories.length} stor${toValidateStories.length > 1 ? "ies" : "y"}`,
  ].filter(Boolean).join(" · ");
  const validated = publications.filter((p) => p.status === "valide" || p.validation_status === "valide");
  const valideeGroups = [
    ["validees", "Validées", validated.filter((p) => !["programme", "publie"].includes(p.status))],
    ["programmees", "Programmées", validated.filter((p) => p.status === "programme")],
    ["publiees", "Publiées", validated.filter((p) => p.status === "publie")],
  ];
  const valideeList = valideeGroups.find(([v]) => v === valideeSub)[2];
  const feedPubs = publications.filter((p) => p.platform === "instagram");
  const calPubs = publications.filter((p) => p.status !== "a_creer");

  const validate = async (p) => {
    await api.post(`/review/${token}/publications/${p.id}/validate`);
    toast.success(`« ${p.title} » validée — merci !`);
    load();
  };

  const requestChanges = async (p) => {
    const text = comment.trim();
    if (!text) { toast.error("Décrivez la modification souhaitée"); return; }
    await api.post(`/review/${token}/publications/${p.id}/request-changes`, { text, author_name: name });
    toast.success("Demande de modification envoyée");
    setComment(""); setChangesPub(null);
    load();
  };

  const renderCard = (p, scrollId = true) => (
    <PubCard key={p.id} p={p} scrollId={scrollId} client={client} changesPub={changesPub}
      comment={comment} setComment={setComment} setChangesPub={setChangesPub}
      onValidate={validate} onRequestChanges={requestChanges} />
  );

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="review-page">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 sm:py-4 flex flex-wrap items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-heading font-bold shrink-0" style={{ background: client.color }}>
            {client.logo_url ? <img src={client.logo_url} className="w-full h-full object-cover rounded-xl" alt="" /> : client.name[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="font-heading font-bold text-lg leading-tight truncate" data-testid="review-client-name">{client.name}</h1>
              {toValidate.length > 0 && (
                <span className="bg-orange-500 text-white text-[10px] font-semibold rounded-full px-2 py-0.5 shrink-0" data-testid="review-pending-chip">
                  {pendingLabel} à valider
                </span>
              )}
            </div>
            <p className="text-xs text-neutral-400">Vos contenus à valider</p>
          </div>
          <div className="shrink-0">
            <NewRequestButton token={token} onCreated={load} />
          </div>
          <div className="w-full">
            <ClientPushSettings token={token} />
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-2 sm:px-4 flex gap-1 overflow-x-auto" data-testid="review-tabs">
          {[["valider", `À valider (${toValidate.length})`],
            ["contenus", `Contenus à fournir (${(data.content_requests || []).filter((r) => r.status !== "valide").length})`],
            ["demandes", "Mes demandes"],
            ["calendrier", "Calendrier"],
            ["validees", `Publications validées (${validated.length})`],
            ["feed", "Aperçu du feed"]].map(([v, l]) => (
            <button key={v} data-testid={`review-tab-${v}`}
              onClick={() => {
                setTab(v);
                if (v === "demandes" && (data.unseen_requests_count || 0) > 0) {
                  api.post(`/review/${token}/requests/mark-seen`).then(load).catch(() => {});
                }
              }}
              className={`px-3 sm:px-4 py-2.5 text-xs sm:text-sm border-b-2 transition-colors shrink-0 whitespace-nowrap ${tab === v ? "border-neutral-900 font-medium" : "border-transparent text-neutral-400 hover:text-neutral-700"}`}>
              {l}
              {v === "demandes" && (data.unseen_requests_count || 0) > 0 && (
                <span className="ml-1.5 bg-red-500 text-white text-[10px] font-semibold rounded-full px-1.5 py-0.5" data-testid="review-demandes-badge">
                  {data.unseen_requests_count}
                </span>
              )}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4 pb-24">
        {toValidate.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3.5 flex flex-wrap items-center gap-3 animate-fade-up" data-testid="review-pending-banner">
            <div className="w-9 h-9 rounded-full bg-orange-500 text-white flex items-center justify-center font-heading font-bold text-sm shrink-0">
              {toValidate.length}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-orange-900">
                {pendingLabel} à valider
              </p>
              <p className="text-xs text-orange-700/70">Validez ou demandez vos modifications pour débloquer la publication.</p>
            </div>
            {tab !== "valider" && (
              <Button size="sm" data-testid="review-pending-cta" onClick={() => setTab("valider")}
                className="bg-orange-500 hover:bg-orange-600 text-white shrink-0">
                Valider maintenant
              </Button>
            )}
          </div>
        )}
        {!name && (
          <div className="bg-white border border-neutral-200 rounded-xl p-4 flex gap-2 items-center">
            <Input data-testid="reviewer-name" placeholder="Votre prénom (pour signer vos commentaires)" value={name}
              onChange={(e) => { setName(e.target.value); localStorage.setItem("sf-reviewer", e.target.value); }} />
          </div>
        )}

        {tab === "feed" && (
          <div className="bg-white border border-neutral-200 rounded-xl p-4">
            <FeedGrid pubs={feedPubs} clientColor={client.color} showDate
              onPubClick={(p) => { if (p.status === "modification") return; setTab(p.status === "a_valider" ? "valider" : "validees"); setTimeout(() => document.getElementById(`review-pub-${p.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150); }} />
          </div>
        )}

        {tab === "contenus" && (
          <ClientContentRequests token={token} requests={data.content_requests || []} onChanged={load} />
        )}
        {tab === "demandes" && (
          <ClientRequestsList token={token} requests={data.client_requests || []} quotes={data.quotes || []} onChanged={load} />
        )}
        {tab === "calendrier" && (
          <CalendarMonth pubs={calPubs} colorOf={() => client.color} readOnly
            month={month} setMonth={setMonth}
            onPubClick={(p) => setDetailPub(p)} />
        )}

        {tab === "valider" && toValidate.length > 0 && (
          <div className="flex gap-2" data-testid="review-valider-subnav">
            {[["pubs", `Publications (${toValidatePubs.length})`], ["stories", `Stories (${toValidateStories.length})`]].map(([v, l]) => (
              <button key={v} data-testid={`review-subtab-${v}`} onClick={() => setValiderSub(v)}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                  validerSub === v
                    ? "bg-neutral-900 text-white border-neutral-900 font-medium"
                    : "bg-white text-neutral-500 border-neutral-200 hover:border-neutral-400"}`}>
                {l}
              </button>
            ))}
          </div>
        )}

        {tab === "valider" && validerList.map((p) => renderCard(p))}

        {tab === "valider" && toValidate.length > 0 && validerList.length === 0 && (
          <div className="bg-white border border-neutral-200 rounded-xl p-10 text-center" data-testid="review-subtab-empty">
            <CheckCircle2 className="w-8 h-8 mx-auto text-green-500 mb-2" />
            <p className="font-medium text-sm">{validerSub === "stories" ? "Aucune story à valider" : "Aucune publication à valider"}</p>
            <button data-testid="review-subtab-switch" onClick={() => setValiderSub(validerSub === "stories" ? "pubs" : "stories")}
              className="mt-2 text-sm text-orange-600 hover:underline">
              Voir {validerSub === "stories" ? `les publications (${toValidatePubs.length})` : `les stories (${toValidateStories.length})`} →
            </button>
          </div>
        )}

        {tab === "validees" && validated.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="review-validees-subnav">
            {valideeGroups.map(([v, label, list]) => (
              <button key={v} data-testid={`review-subtab-${v}`} onClick={() => setValideeSub(v)}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                  valideeSub === v
                    ? "bg-neutral-900 text-white border-neutral-900 font-medium"
                    : "bg-white text-neutral-500 border-neutral-200 hover:border-neutral-400"}`}>
                {label} ({list.length})
              </button>
            ))}
          </div>
        )}

        {tab === "validees" && valideeList.map((p) => renderCard(p))}

        {tab === "validees" && validated.length > 0 && valideeList.length === 0 && (() => {
          const next = valideeGroups.find(([, , l]) => l.length > 0);
          return (
            <div className="bg-white border border-neutral-200 rounded-xl p-10 text-center" data-testid="review-validees-empty">
              <CheckCircle2 className="w-8 h-8 mx-auto text-green-500 mb-2" />
              <p className="font-medium text-sm">Aucune publication dans cette rubrique</p>
              {next && (
                <button data-testid="review-validees-switch" onClick={() => setValideeSub(next[0])}
                  className="mt-2 text-sm text-orange-600 hover:underline">
                  Voir les {next[1].toLowerCase()} ({next[2].length}) →
                </button>
              )}
            </div>
          );
        })()}

        {tab === "validees" && validated.length === 0 && (
          <div className="bg-white border border-neutral-200 rounded-xl p-12 text-center" data-testid="review-no-validated">
            <CheckCircle2 className="w-10 h-10 mx-auto text-neutral-300 mb-3" />
            <p className="font-heading font-bold text-lg">Aucune publication validée</p>
            <p className="text-sm text-neutral-500 mt-1">Les publications que vous approuvez apparaîtront ici.</p>
          </div>
        )}

        {tab === "valider" && toValidate.length === 0 && (
          <div className="bg-white border border-neutral-200 rounded-xl p-12 text-center" data-testid="review-all-done">
            <CheckCircle2 className="w-10 h-10 mx-auto text-green-500 mb-3" />
            <p className="font-heading font-bold text-lg">Tout est validé</p>
            <p className="text-sm text-neutral-500 mt-1">Aucun contenu n'attend votre validation pour le moment.</p>
          </div>
        )}
      </main>

      <Dialog open={!!detailPub} onOpenChange={(v) => !v && setDetailPub(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-0 border-0 bg-transparent shadow-none" data-testid="review-pub-detail-modal" aria-describedby={undefined}>
          <DialogTitle className="sr-only">{detailPub ? detailPub.title : "Détail de la publication"}</DialogTitle>
          {detailPub && renderCard(detailPub, false)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
